import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { GitBranch, Users, ShieldCheck, Compass, Check } from "lucide-react";
import { cn } from "@/design/cn";
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  NarrativeCard,
  Card,
  CardBody,
  Button,
} from "@/design/components";
import { useActiveProfile, useActiveWorkspace } from "@/domain/hooks";
import { useAuthStore } from "@/domain/authStore";
import { useStore } from "@/domain/store";
import {
  resolveGuideKey,
  navItemsForGuideKey,
  ROLE_GUIDE_CONTENT,
  hasSeenGuide,
  markGuideSeen,
  isGuideDismissedForever,
  setGuideDismissedForever,
  type GuideChainStep,
} from "@/domain/roleGuide";

type GuideCtx = { openGuide: () => void };
const Ctx = createContext<GuideCtx>({ openGuide: () => {} });

/** Opens the current persona's "how this works for you" guide - the sidebar
 * (Sidebar.tsx) and the reporter top bar (ReporterShell.tsx) both call this
 * to open it manually, regardless of the auto-show/dismissal state below. */
export function useRoleGuide(): GuideCtx {
  return useContext(Ctx);
}

const GOVERNANCE_CONTROLS = [
  {
    icon: GitBranch,
    title: "Delegated authority",
    body: "Each level sets the spend threshold for the level below. Above it, spend escalates up the chain.",
  },
  {
    icon: Users,
    title: "Segregation of duties",
    body: "The person who raises a spend ask cannot also approve it. Approval always rests with a different authoriser.",
  },
  {
    icon: ShieldCheck,
    title: "Multi-signature",
    body: "Large milestone disbursements require two distinct funder sign-offs before the disbursement is released.",
  },
];

/**
 * The real approval chain, as a vertical numbered list — not WizardProgress
 * (design/components/Wizard.tsx), whose non-compact mode lays labelled steps
 * out in one non-wrapping horizontal row: at 7 steps, real labels like
 * "Evidence submitted" don't fit even the drawer's own 640px width, forcing
 * an unsignalled horizontal scroll on every screen size. A vertical list
 * wraps naturally at any width instead, which matters most for the persona
 * this app is actually built around (reporter, mobile, often one-handed).
 */
function ChainStepper({ steps, currentIndex }: { steps: GuideChainStep[]; currentIndex: number }) {
  return (
    <ol>
      {steps.map((step, i) => {
        const state = i < currentIndex ? "done" : i === currentIndex ? "current" : "upcoming";
        const isLast = i === steps.length - 1;
        return (
          <li key={step.key} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
                  state === "done" && "bg-ok text-white",
                  state === "current" && "bg-brand text-white ring-2 ring-brandRing",
                  state === "upcoming" && "border border-line bg-card text-faint",
                )}
              >
                {state === "done" ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
              </span>
              {!isLast && <span className="w-px flex-1 bg-line" aria-hidden />}
            </div>
            <span className={cn("pb-4 text-sm", state === "current" ? "font-medium text-ink" : "text-mute")}>
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Mounted once at the app root (App.tsx), alongside TraceProvider - a
 * provider that mounts exactly once for the life of the session is what
 * makes the auto-open-once-per-profile effect below safe, since several
 * shells underneath it (WorkspaceShell.tsx in particular) remount on every
 * navigation and would otherwise reopen the guide repeatedly.
 */
export function RoleGuideProvider({ children }: { children: ReactNode }) {
  const profile = useActiveProfile();
  const workspace = useActiveWorkspace();
  const authUser = useAuthStore((s) => s.user);
  const devMode = useStore((s) => s.devMode);
  // A genuinely signed-in session (real login, or demo mode with a persona
  // selected) - guards the auto-open effect below. Without this, logging
  // out (authUser -> null) makes useActiveProfile() fall back to the seed
  // default profile, which reads to this effect as "a brand new profile,
  // never seen" and auto-opens the guide right around/after logout, even
  // though this provider is mounted above the auth boundary and would
  // happily show it on /login too.
  const signedIn = devMode || !!authUser;
  const [open, setOpen] = useState(false);
  // Mirrors the persisted "dismissed forever" flag in real React state (not
  // just read fresh from localStorage at render time) so the checkbox
  // re-renders the instant it's clicked, rather than relying on the browser's
  // own native checkbox behavior to paper over React not re-rendering.
  const [dismissedForever, setDismissedForever] = useState(() => isGuideDismissedForever(profile.id));

  const guideKey = resolveGuideKey(profile);

  // Auto-open once per profile per session, unless that profile has
  // explicitly opted out forever. hasSeenGuide is session-scoped
  // (sessionStorage) specifically so it does NOT alone block every future
  // auto-open the way a permanent flag would - that was the guide's original
  // bug: "seen" and "dismissed forever" collapsed into the same permanent
  // signal, so the dismiss checkbox below had nothing left to control.
  useEffect(() => {
    if (!signedIn) {
      setOpen(false);
      return;
    }
    setDismissedForever(isGuideDismissedForever(profile.id));
    if (!guideKey) return;
    if (!hasSeenGuide(profile.id) && !isGuideDismissedForever(profile.id)) {
      setOpen(true);
      markGuideSeen(profile.id);
    }
  }, [signedIn, profile.id, guideKey]);

  function openGuide() {
    if (!guideKey) return;
    markGuideSeen(profile.id);
    setOpen(true);
  }

  function handleDismissedForeverChange(checked: boolean) {
    setDismissedForever(checked);
    setGuideDismissedForever(profile.id, checked);
  }

  const content = guideKey ? ROLE_GUIDE_CONTENT[guideKey] : null;
  const navItems = guideKey ? navItemsForGuideKey(guideKey, `/workspace/${workspace.id}`) : [];

  return (
    <Ctx.Provider value={{ openGuide }}>
      {children}
      {content && (
        <Drawer open={open} onOpenChange={setOpen} width="640px">
          <DrawerHeader>
            <p className="text-meta text-faint">{content.roleLabel}</p>
            <DrawerTitle className="text-base font-semibold tracking-tight text-ink">
              How this works for you
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              A guide to what your role can do, how approval works, and where to find things.
            </DrawerDescription>
          </DrawerHeader>

          <DrawerBody className="space-y-6">
            <NarrativeCard variant="briefing" title="Where you fit in" body={content.framing} />

            <section>
              <p className="mb-3 text-sm font-semibold text-ink">Where you sit in the approval chain</p>
              <ChainStepper steps={content.chainSteps} currentIndex={content.currentStepIndex} />
            </section>

            <Card>
              <CardBody>
                <p className="text-sm font-semibold text-ink">What you can do</p>
                <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-mute">
                  {content.capabilities.map((c) => (
                    <li key={c} className="flex gap-2">
                      <span aria-hidden>•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <p className="text-sm font-semibold text-ink">Who you report to</p>
                <p className="mt-1.5 text-sm leading-relaxed text-mute">{content.reportingLine}</p>
              </CardBody>
            </Card>

            {navItems.length > 0 && (
              <Card>
                <CardBody>
                  <p className="text-sm font-semibold text-ink">Where to find things</p>
                  <ul className="mt-2 space-y-3">
                    {navItems.map((n) => (
                      <li key={n.to} className="flex items-start gap-2">
                        <n.icon className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-hidden />
                        <div>
                          <p className="text-sm text-ink">{n.label}</p>
                          {n.description && <p className="text-xs leading-relaxed text-mute">{n.description}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            )}

            <section>
              <p className="mb-3 text-sm font-semibold text-ink">How the wider system works</p>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {GOVERNANCE_CONTROLS.map((c) => (
                  <div key={c.title} className="rounded-lg border border-line bg-card p-4">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brandSoft text-brand">
                      <c.icon className="h-4 w-4" />
                    </span>
                    <p className="mt-2 text-sm font-semibold text-ink">{c.title}</p>
                    <p className="mt-1 text-xs leading-snug text-mute">{c.body}</p>
                  </div>
                ))}
              </div>
            </section>
          </DrawerBody>

          <DrawerFooter className="justify-between">
            <label className="flex items-center gap-2 text-sm text-mute">
              <input
                type="checkbox"
                checked={dismissedForever}
                onChange={(e) => handleDismissedForeverChange(e.target.checked)}
                className="h-4 w-4 rounded border-line"
              />
              Don't show this automatically next time I use this role
            </label>
            <Button onClick={() => setOpen(false)}>Got it</Button>
          </DrawerFooter>
        </Drawer>
      )}
    </Ctx.Provider>
  );
}

/** Small entry-point icon, shared so the sidebar and reporter top bar render
 * an identical affordance. */
export const RoleGuideTriggerIcon = Compass;
